import { Component, Input, OnInit, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { Role } from 'app/shared/models/role';
import { Permissionset } from 'app/shared/models/permissionset';
import {
  tr,
  IQueryResult,
  DialogService,
  SearchConfig,
  Types,
  KeyBase,
  DialogResult,
  chainAndForkJoinObservables,
} from '@kpd/framework-ngx';
import { RoleService } from 'app/shared/services/role.service';
import { RoleKey } from 'app/shared/models/keys/role-key';
import { switchMap, map, filter, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { PermissionsetService } from 'app/shared/services/permissionset.service';
import { PermissionsetKey } from 'app/shared/models/keys/permissionset-key';
import { TreeNode } from 'primeng/api';
import { RoleFacade } from 'app/shared/services/facades/role.facade';
import { Permissions } from 'app/shared/services/permissions';

@Component({
  selector: 'app-role-details-permissionsets',
  templateUrl: './role-details-permissionsets.component.html',
})
export class RoleDetailsPermissionsetsComponent implements OnInit, OnChanges {
  Permissions: typeof Permissions = Permissions;
  @Input()
  item: Role;
  saveErrors: string[] = [];
  selection: TreeNode[] = [];
  noPermissionSets = tr('No permission sets linked to this role');
  permissionSetsTree: TreeNode[];
  @Output()
  itemChanged = new EventEmitter<number>();
  linkPermissionSetsSearchConfig = this.createLinkPermissionSetsSearchConfig();
  linkPermissionSetsCols = this.createLinkPermissionSetsCols();
  permissionSetTypes = [
    { type: 'assign', display: tr('Assign') },
    { type: 'use', display: tr('Use') },
    { type: 'both', display: tr('Both') },
  ];

  constructor(
    private roleService: RoleService,
    private roleFacade: RoleFacade,
    private permissionSetService: PermissionsetService,
    private dialogService: DialogService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.item && this.item instanceof Role && this.item.permissionSets) {
      this.setupTree();
    }
  }

  private setupTree(): void {
    this.selection = [];
    if (this.item.permissionSets) {
      this.permissionSetsTree = this.item.permissionSets.map(set => {
        return {
          data: set,
          label: set.name,
          children: [],
          leaf: false,
          collapsed: true,
          styleClass: 'permissionsets-tree__set',
        } as TreeNode;
      });
    }
  }

  createPermissionsNodes(set: Permissionset): TreeNode[] {
    if (!set.permissions || set.permissions.length === 0) {
      return [
        {
          data: undefined,
          leaf: true,
          selectable: false,
          styleClass: 'permissionsets-tree__permission',
          label: tr('No permissions linked to this permission set'),
          type: 'notFound',
        } as TreeNode,
      ];
    }
    return set.permissions.map(permission => {
      return {
        data: permission,
        selectable: false,
        leaf: true,
        label: permission.getDisplayName(),
        styleClass: 'permissionsets-tree__permission',
      } as TreeNode;
    });
  }

  ngOnInit(): void {
    this.setupTree();
  }

  onNodeExpand(event: any): void {
    if (event.node) {
      this.roleFacade.detailLoadingStarted();
      this.permissionSetService.getById(event.node.data.key).subscribe(permissionSetWithPermissions => {
        if (permissionSetWithPermissions) {
          event.node.children = this.createPermissionsNodes(permissionSetWithPermissions);
          event.node.data.permissions = permissionSetWithPermissions.permissions;
        }
        this.roleFacade.detailLoadingFinished();
      });
    }
  }

  onAdd(): void {
    this.roleFacade.detailLoadingStarted();

    this.permissionSetService
      .getUserPermissionSets(this.item.application as string)
      .pipe(
        switchMap(permissionSets => {
          const excludeKeys = (this.item.permissionSets as Permissionset[]).map(s => s.key);
          const allSets = permissionSets.filter(set => {
            return set.type !== 'template';
          });
          const inputData: { queryResult: IQueryResult; keysToExclude: KeyBase[] } = {
            queryResult: { result: allSets, totalCount: allSets.length },
            keysToExclude: excludeKeys,
          };
          this.roleFacade.detailLoadingFinished();
          return this.dialogService.open('linkEntities', inputData).afterClosed();
        }),
      )
      .subscribe((selectedKeys?: PermissionsetKey[]) => {
        if (!selectedKeys) {
          return;
        }
        this.saveErrors = [];

        this.roleFacade.detailLoadingStarted();
        // re-load permissionSets after a successful add
        const observers = selectedKeys.map(key => {
          return this.roleService.addPermissionSetToRole(this.item.key as RoleKey, key.permissionSetId).pipe(
            catchError(() => {
              this.saveErrors.push(tr('Following permission set could not be added: ') + key.toString());
              this.roleFacade.detailLoadingFinished();
              return of(null);
            }),
          );
        });
        chainAndForkJoinObservables(observers).subscribe(() => {
          this.roleService.getById(this.item.key, true).subscribe(role => {
            this.item.permissionSets = role.permissionSets;
            this.setupTree();
            this.itemChanged.emit((this.item.permissionSets as Permissionset[]).length);
          });
          this.roleFacade.detailLoadingFinished();
        });
      });
  }

  loadPermissions(queryResult: IQueryResult): Observable<IQueryResult> {
    const observables: Observable<Permissionset>[] = [];

    queryResult.result.map((permSet: Permissionset) => {
      const obs = this.permissionSetService.getPermissionSetPermissions(permSet.key).pipe(
        map(perms => {
          permSet.permissions = perms;
          return permSet;
        }),
      );
      observables.push(obs);
    });

    return chainAndForkJoinObservables(observables).pipe(
      map(permissionSets => {
        return { result: permissionSets, totalCount: queryResult.totalCount };
      }),
    );
  }

  onDelete(): void {
    this.dialogService
      .open('confirmRolePermissionSetDelete', {
        message: tr('Are you sure you want to unassign the permission set(s)?'),
        header: tr('Unassign permission set(s)'),
        verifyButtonText: tr('Unassign'),
        verifyButtonStyle: 'danger',
        cancelButtonText: tr('Cancel'),
      })
      .afterClosed()
      .subscribe(dialogResult => {
        this.saveErrors = [];
        if (dialogResult === DialogResult.Verify) {
          for (const selectedNode of this.selection) {
            this.roleFacade.detailLoadingStarted();
            this.roleService
              .removePermissionSetFromRole(this.item.key as RoleKey, selectedNode.data.name)
              .pipe(
                catchError(() => {
                  this.saveErrors.push(
                    tr('Following permission set could not be unassigned: ') + selectedNode.data.name,
                  );
                  return of(null);
                }),
              )
              .pipe(filter(result => result !== null))
              .subscribe(() => {
                this.item.permissionSets = (this.item.permissionSets as Permissionset[]).filter(
                  set => set.name !== selectedNode.data.name,
                );
                this.setupTree();
                this.roleFacade.detailLoadingFinished();
                this.itemChanged.emit(this.item.permissionSets.length);
              });
          }
        }
      });
  }

  isRoleAvailable(): boolean {
    return this.item instanceof Role; // Can be NullEntity
  }

  createLinkPermissionSetsSearchConfig(): SearchConfig {
    return new SearchConfig({
      multipleCriteria: true,
      fields: [
        {
          field: 'name',
          displayValue: tr('Name'),
          dataType: Types.String,
        },
        {
          field: 'translatedDescription',
          displayValue: tr('Description'),
          dataType: Types.String,
        },
        {
          field: 'type',
          displayValue: tr('Type'),
          dataType: Types.Entity,
          entityConfig: {
            displayField: 'display',
            lookupField: 'type',
            completeMethod: (value: string) => {
              if (!value) {
                return of([...this.permissionSetTypes]);
              }
              return of([
                ...this.permissionSetTypes.filter(permissionSet => {
                  return permissionSet.display.toUpperCase().includes(value.toUpperCase());
                }),
              ]);
            },
          },
        },
      ],
    });
  }

  createLinkPermissionSetsCols(): any[] {
    return [
      { field: 'name', header: tr('Name'), width: '30%' },
      { field: 'translatedDescription', header: tr('Description'), width: '70%' },
      { field: 'type', header: tr('Type'), width: '150px', type: 'type' },
      { field: 'isKpdSet', header: 'KPD', width: '70px', type: 'isKpdSet' },
    ];
  }
}
